import * as THREE from "three";
import { logger } from "../utils/logger";
import { sanitizeMjcfName } from "../utils/mjcfNames";
import { addBodyLabelMarker } from "./process-body/bodyLabelMarker";
import { attachBodyJoints } from "./process-body/jointGeoms";
import { attachMeshGeoms } from "./process-body/meshGeoms";
import { attachPrimitiveGeoms } from "./process-body/primitiveGeoms";
import { attachBodySites, attachUnparentedSitesToModelGroup } from "./process-body/sites";
import { attachInertial } from "./process-body/inertials";

/**
 * Processes a body from the store and creates the corresponding 3D scene objects
 * This is the core function that builds the scene from the Zustand store data
 *
 * @param {Object} bodyNode - The body from the store
 * @param {THREE.Group} parent - The parent Three.js group to add objects to
 * @param {THREE.Quaternion} parentRot - Accumulated rotation from parent bodies
 * @param {THREE.Vector3} parentPos - Accumulated position from parent bodies
 * @param {Object} options - Configuration options
 * @param {Object} options.sceneActions - Scene store actions
 * @param {Object} options.materials - Materials from the store
 * @param {Object} options.fileMap - Map of available files
 * @param {Object} options.loader - OBJ loader instance
 * @param {Object} options.stlLoader - STL loader instance
 * @param {Object} options.sceneObjectsRef - Reference to store scene objects
 * @param {Object} options.jointObjectsRef - Reference to store joint objects
 * @param {Object} options.siteLabelsRef - Reference to store site labels
 * @param {Object} options.showJoints - Whether to show joints
 * @param {Object} options.showCollision - Whether to show collision geometries
 * @param {Object} options.showVisual - Whether to show visual meshes
 * @param {Object} options.showSites - Whether to show sites
 * @param {Object} options.showInertials - Whether to show inertia ellipsoids
 * @param {Object} options.jointMarkerSizes - Sizes for joint markers
 * @param {Object} options.sceneRef - Reference to the main scene
 */
export function processBodyFromStore(
  bodyNode,
  parent,
  parentRot = new THREE.Quaternion(),
  parentPos = new THREE.Vector3(),
  options = {}
) {
  const {
    sceneActions,
    materials,
    sceneObjectsRef,
    jointObjectsRef,
    siteLabelsRef,
    showJoints,
    showCollision,
    showVisual,
    showSites,
    showInertials,
    jointMarkerSizes,
    sceneRef,
    registerPendingMeshLoad,
  } = options;

  const group = new THREE.Group();
  if (bodyNode.name) group.name = bodyNode.name;
  group.userData.isBodyContainer = true;
  group.userData.type = "body";
  group.userData.bodyName = bodyNode.name;
  group.userData.bodyUuid = bodyNode.uuid;

  // Pose wrapper (receives physics/kinematics), keep meshes under a separate visual group
  const poseGroup = new THREE.Group();
  poseGroup.name = bodyNode.name ? `${bodyNode.name}_pose` : "pose";
  poseGroup.userData.isPoseGroup = true;
  // IMPORTANT: sceneObjectsRef stores poseGroup, so it must carry body identity for
  // incremental transform sync (useSceneBuilder) and other UUID-based lookups.
  poseGroup.userData.bodyUuid = bodyNode.uuid;
  poseGroup.userData.bodyName = bodyNode.name;
  group.add(poseGroup);
  const visualGroup = new THREE.Group();
  visualGroup.name = bodyNode.name ? bodyNode.name + "_visual" : "visual";
  poseGroup.add(visualGroup);

  // Set position and rotation from store
  const bodyPos = Array.isArray(bodyNode.position) ? bodyNode.position : [0, 0, 0];
  const bodyQuat = Array.isArray(bodyNode.orientation) ? bodyNode.orientation : [1, 0, 0, 0];

  group.position.set(bodyPos[0], bodyPos[1], bodyPos[2]);
  group.setRotationFromQuaternion(
    new THREE.Quaternion(bodyQuat[1], bodyQuat[2], bodyQuat[3], bodyQuat[0])
  );

  // Move body transform onto poseGroup so wrapper receives kinematics/physics
  poseGroup.position.copy(group.position);
  poseGroup.quaternion.copy(group.quaternion);
  group.position.set(0, 0, 0);
  group.quaternion.identity();
  parent.add(group);

  // Key by UUID and sanitized UUID for physics worker compatibility
  if (bodyNode.uuid) {
    sceneObjectsRef.current[bodyNode.uuid] = poseGroup;
    // Also store by sanitized UUID (physics worker uses sanitized names)
    const sanitizedUuid = sanitizeMjcfName(bodyNode.uuid);
    if (sanitizedUuid !== bodyNode.uuid) {
      sceneObjectsRef.current[sanitizedUuid] = poseGroup;
    }
  } else {
    logger.error(
      `[processBodyFromStore] Missing body uuid for "${bodyNode.name || "unnamed"}"; cannot register scene object.`
    );
  }

  addBodyLabelMarker(bodyNode, poseGroup);

  // Calculate accumulated rotation and position (compose properly)
  const accumulatedRot = parentRot.clone().multiply(poseGroup?.quaternion || group.quaternion);
  const accumulatedPos = parentPos
    .clone()
    .add((poseGroup?.position || group.position).clone().applyQuaternion(parentRot));

  attachBodyJoints({
    bodyNode,
    group,
    poseGroup,
    parentRot,
    parentPos,
    accumulatedRot,
    sceneActions,
    jointObjectsRef,
    showJoints,
    jointMarkerSizes,
  });

  attachMeshGeoms({
    bodyNode,
    visualGroup,
    materials,
    showCollision,
    showVisual,
    registerPendingMeshLoad,
  });

  attachPrimitiveGeoms({
    bodyNode,
    visualGroup,
    materials,
    showCollision,
    showVisual,
  });

  attachBodySites({ bodyNode, poseGroup, siteLabelsRef, showSites });

  attachInertial({ bodyNode, poseGroup, showInertials, sceneRef });

  // Process children recursively
  if (bodyNode.children && bodyNode.children.length > 0) {
    bodyNode.children.forEach((child) => {
      // Validate that child is a proper body object, not just a UUID string
      if (typeof child === "string") {
        logger.error(
          `[processBodyFromStore] Child of body "${bodyNode.name}" is a UUID string "${child}" instead of body object. ` +
            `This indicates getBodyTree() failed to reconstruct the child hierarchy.`
        );
        return;
      }
      const childGroup = new THREE.Group();
      if (child.name) childGroup.name = child.name;
      poseGroup.add(childGroup);
      processBodyFromStore(child, childGroup, accumulatedRot, accumulatedPos, options);
    });
  }
}

export { attachUnparentedSitesToModelGroup };
